/**
 * Where the Agent Details card gets its data (#35737).
 *
 * The card originally read the account's most recent active/future policy (Customer page). It is
 * now also shown on the Policy page (for the policy being viewed, in any status) and on the Offer
 * page (for the offer itself, which may not have a customer record yet). These helpers map each
 * record to the common shape the card renders from.
 */

/** @typedef {{ affinity: string|null, salesRep: string|null, bindRep: string|null, leadSource: string|null }} AgentDetailsSource */

/**
 * @param {object|null|undefined} policy — a policy from the account/policy store (PolicyFragment)
 * @returns {AgentDetailsSource|null}
 */
export const agentDetailsSourceFromPolicy = (policy) => {
  if (!policy) {
    return null;
  }
  return {
    affinity: policy.segments?.[0]?.global?.affinity ?? null,
    salesRep: policy.salesRep ?? null,
    bindRep: policy.bindRep ?? null,
    leadSource: policy.offer?.quote?.leadSource ?? null
  };
};

/**
 * @param {object|null|undefined} offer — the offer store's offer (OfferFragment)
 * @returns {AgentDetailsSource|null} — Sold Producer is only known once the offer is bound, so bindRep is null.
 */
export const agentDetailsSourceFromOffer = (offer) => {
  if (!offer?.quote) {
    return null;
  }
  return {
    affinity: offer.quote.global?.affinity ?? null,
    salesRep: offer.quote.rep ?? null,
    bindRep: null,
    leadSource: offer.quote.leadSource ?? null
  };
};

/**
 * The policy's producer: the sales rep, else the rep on the offer it was bound from.
 * @param {object|null|undefined} policy
 * @returns {string|null}
 */
export const getPolicyProducer = (policy) => policy?.salesRep || policy?.offer?.quote?.rep || null;
